import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import FormField from '@/common/PassengerTools/FormField';

function openExpireLookupPopup(): Window | null {
  return window.open(
    'about:blank',
    'passenger-expire-lookup',
    'width=240,height=240,left=20,top=20'
  );
}

async function lookupAcquisitionExternalIdViaPopup(
  appraisalId: string,
  existingPopup?: Window | null
): Promise<string> {
  return new Promise((resolve, reject) => {
    const url = `${window.location.origin}/sell-my-car/offer/${appraisalId}`;
    const popup = existingPopup ?? openExpireLookupPopup();
    if (!popup) {
      reject(
        new Error('Popup blocked — allow popups for this site and retry.')
      );

      return;
    }
    popup.location.href = url;

    const startTime = Date.now();
    const interval = window.setInterval(() => {
      try {
        if (popup.closed) {
          window.clearInterval(interval);
          reject(new Error('Popup closed before the acquisition call fired.'));

          return;
        }
        const entries = popup.performance.getEntriesByType('resource');
        for (const entry of entries) {
          const match = entry.name.match(
            /\/stc\/trades\/api\/v\d+\/acquisition\/(ACQ[a-f0-9]+)/i
          );
          if (match) {
            window.clearInterval(interval);
            try {
              popup.close();
            } catch {
              // already closed
            }
            resolve(match[1]);

            return;
          }
        }
        if (Date.now() - startTime > 15000) {
          window.clearInterval(interval);
          try {
            popup.close();
          } catch {
            // already closed
          }
          reject(
            new Error(
              'Popup lookup timed out — no v6/acquisition call detected in 15s.'
            )
          );
        }
      } catch (error) {
        window.clearInterval(interval);
        try {
          popup.close();
        } catch {
          // already closed
        }
        reject(error as Error);
      }
    }, 250);
  });
}

async function fetchNumericAcquisitionIdFromExternal(
  externalId: string
): Promise<string> {
  const res = await fetch(
    `https://apik.carvanatech.com/stc/trades/api/v6/acquisition/${externalId}`
  );
  if (!res.ok) {
    throw new Error(`v6/acquisition lookup ${res.status} ${res.statusText}`);
  }
  const body = (await res.json()) as {
    content?: { acquisition?: { acquisitionId?: string } };
  };
  const id = body.content?.acquisition?.acquisitionId;
  if (!id) {
    throw new Error('v6/acquisition response missing numeric acquisitionId');
  }

  return String(id);
}

import {
  Button as StyledButton,
  Heading,
  Input
} from './PassengerTools.styles';
import SoftPullSection from './Users/SoftPullSection';
import TradeSection from './Users/TradeSection';
import {
  DEFAULT_TRADE_CONFIG,
  TradeConfig,
  getTradeVin,
  isPendingMissingVin
} from './Users/TradeSection';
import UsersList from './Users/UsersList';
import {
  CreatedUser,
  TradeOptions,
  UserAccountType,
  addSoftPullToCurrentUser,
  addTradeToCurrentUser,
  clearTestUsersFromLocalStorage,
  createCommonUser,
  expireMostRecentTradeForCurrentUser,
  getCurrentSessionUser,
  getTestUsersFromLocalStorage,
  loginUser,
  logoutUser,
  setSessionUser,
  storeTestUserInLocalStorage
} from './utils/createTestUser';

export default function Users() {
  const [addSecondTrade, setAddSecondTrade] = useState(false);
  const [createdUser, setCreatedUser] = useState<CreatedUser | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [existingUsers, setExistingUsers] = useState<CreatedUser[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [sessionHasTrade, setSessionHasTrade] = useState(false);
  const [expirePanelOpen, setExpirePanelOpen] = useState(false);
  const [expireAcquisitionId, setExpireAcquisitionId] = useState('');
  const [expireStatus, setExpireStatus] = useState<
    'idle' | 'looking-up' | 'ready' | 'failed'
  >('idle');
  const [expireError, setExpireError] = useState('');
  const [name, setName] = useState('');
  const [showExistingUsers, setShowExistingUsers] = useState(false);
  const [softPullApplyOnCreate, setSoftPullApplyOnCreate] = useState(true);
  const [softPullCreditStatus, setSoftPullCreditStatus] = useState<
    'Excellent' | 'Fair'
  >('Excellent');
  const [softPullExpired, setSoftPullExpired] = useState(false);
  const [softPullOpen, setSoftPullOpen] = useState(false);
  const [trade1, setTrade1] = useState<TradeConfig>({
    ...DEFAULT_TRADE_CONFIG
  });
  const [trade2, setTrade2] = useState<TradeConfig>({
    ...DEFAULT_TRADE_CONFIG
  });
  const [tradeApplyOnCreate, setTradeApplyOnCreate] = useState(true);
  const [tradeOpen, setTradeOpen] = useState(false);

  useEffect(() => {
    const sessionUser = getCurrentSessionUser();
    setIsLoggedIn(!!sessionUser?.userId);
    setSessionHasTrade(
      !!sessionUser?.acquisitionId ||
        (sessionUser?.acquisitionIds?.length ?? 0) > 0
    );
  }, []);

  const buildTradeOptions = useCallback(
    (config: TradeConfig): TradeOptions => ({
      equityLevel: config.equityLevel,
      loanAmount: config.loanAmount
        ? parseInt(config.loanAmount, 10)
        : undefined,
      status: config.status,
      vin: getTradeVin(config)
    }),
    []
  );

  const willApplyTrade = tradeOpen && tradeApplyOnCreate;
  const willApplySoftPull = softPullOpen && softPullApplyOnCreate;

  const determineUserType = useCallback((): UserAccountType => {
    if (!willApplyTrade && !willApplySoftPull) return 'common';
    const tradeIsExpired = willApplyTrade && trade1.status === 'expired';
    if (willApplyTrade && willApplySoftPull) {
      return tradeIsExpired || softPullExpired
        ? 'expired-soft-trade'
        : 'soft-trade';
    }
    if (willApplyTrade) {
      if (addSecondTrade) return 'multiple-trades';

      return tradeIsExpired ? 'expired-trade' : 'tradein';
    }

    return softPullExpired ? 'expired-softpull' : 'softpull';
  }, [
    addSecondTrade,
    softPullExpired,
    trade1.status,
    willApplySoftPull,
    willApplyTrade
  ]);

  const applyConfiguredOptions = useCallback(
    async (user: CreatedUser): Promise<CreatedUser> => {
      const acquisitionIds: string[] = [];
      if (willApplyTrade) {
        acquisitionIds.push(
          await addTradeToCurrentUser(buildTradeOptions(trade1))
        );
        if (addSecondTrade) {
          acquisitionIds.push(
            await addTradeToCurrentUser(buildTradeOptions(trade2))
          );
        }
      }
      if (willApplySoftPull) {
        await addSoftPullToCurrentUser({
          creditStatus: softPullCreditStatus,
          expired: softPullExpired
        });
      }

      return acquisitionIds.length > 0
        ? {
            ...user,
            acquisitionId: acquisitionIds[0],
            acquisitionIds
          }
        : user;
    },
    [
      addSecondTrade,
      buildTradeOptions,
      softPullCreditStatus,
      softPullExpired,
      trade1,
      trade2,
      willApplySoftPull,
      willApplyTrade
    ]
  );

  const handleCreate = useCallback(async () => {
    setIsCreating(true);
    setErrorMessage('');
    try {
      const targetType = determineUserType();
      let user: CreatedUser = {
        ...(await createCommonUser(name || undefined)),
        type: targetType
      };
      if (willApplyTrade || willApplySoftPull) {
        const loggedIn = await loginUser(user, { reload: false });
        user = {
          ...(await applyConfiguredOptions(loggedIn)),
          type: targetType
        };
        await logoutUser({ reload: false });
      }
      storeTestUserInLocalStorage(user);
      setCreatedUser(user);
    } catch (error) {
      setErrorMessage(`Failed to create user: ${error}`);
    } finally {
      setIsCreating(false);
    }
  }, [
    applyConfiguredOptions,
    determineUserType,
    name,
    willApplySoftPull,
    willApplyTrade
  ]);

  const runExpireFlow = useCallback(async (prePopup?: Window | null) => {
    const closePrePopup = () => {
      if (!prePopup) return;
      try {
        prePopup.close();
      } catch {
        // already closed
      }
    };

    const sessionUser = getCurrentSessionUser();
    if (!sessionUser?.userId) {
      closePrePopup();
      setExpireStatus('failed');
      setExpireError('No active session — log in as a test user first.');

      return;
    }

    setExpireError('');
    setExpireAcquisitionId('');
    setExpireStatus('looking-up');

    try {
      const result = await expireMostRecentTradeForCurrentUser();
      closePrePopup();
      setExpireStatus('idle');
      setExpirePanelOpen(false);
      window.alert(
        `Expired trade for current user${
          result.acquisitionId ? ` (acquisition ${result.acquisitionId})` : ''
        }.`
      );
      window.location.reload();

      return;
    } catch (serverRouteError) {
      console.warn(
        '/tools/test/expire-trade unavailable, falling back to popup lookup:',
        serverRouteError
      );
    }

    const appraisalId =
      sessionUser.acquisitionIds?.[0] ?? sessionUser.acquisitionId ?? '';
    if (!appraisalId) {
      closePrePopup();
      setExpireStatus('failed');
      setExpireError(
        'No appraisal ID on the current session user — log in to a user with a trade-in.'
      );

      return;
    }

    try {
      const externalId = await lookupAcquisitionExternalIdViaPopup(
        appraisalId,
        prePopup
      );
      const numericId = await fetchNumericAcquisitionIdFromExternal(externalId);
      setExpireAcquisitionId(numericId);
      setExpireStatus('ready');
    } catch (error) {
      console.warn('Popup lookup failed:', error);
      setExpireStatus('failed');
      setExpireError(String(error));
    }
  }, []);

  const handleCreateAndLogin = useCallback(
    async (prePopup?: Window | null) => {
      setIsCreating(true);
      setErrorMessage('');
      try {
        const targetType = determineUserType();
        const user: CreatedUser = {
          ...(await createCommonUser(name || undefined)),
          type: targetType
        };
        const loggedIn = await loginUser(user, { reload: false });
        const finalUser = {
          ...(await applyConfiguredOptions(loggedIn)),
          type: targetType
        };
        storeTestUserInLocalStorage(finalUser);
        setSessionUser(finalUser);

        const needsExpire = willApplyTrade && trade1.status === 'expired';
        if (needsExpire) {
          setIsCreating(false);
          setExpirePanelOpen(true);
          setExpireStatus('idle');
          setExpireError('');
          setExpireAcquisitionId('');
          void runExpireFlow(prePopup);

          return;
        }

        if (prePopup) {
          try {
            prePopup.close();
          } catch {
            // already closed
          }
        }
        window.location.reload();
      } catch (error) {
        if (prePopup) {
          try {
            prePopup.close();
          } catch {
            // already closed
          }
        }
        setErrorMessage(`Failed to create user: ${error}`);
        setIsCreating(false);
      }
    },
    [
      applyConfiguredOptions,
      determineUserType,
      name,
      runExpireFlow,
      trade1.status,
      willApplyTrade
    ]
  );

  const handleUpdateUser = useCallback(async () => {
    setIsUpdating(true);
    setErrorMessage('');
    try {
      const newAcquisitionIds: string[] = [];
      if (tradeOpen) {
        newAcquisitionIds.push(
          await addTradeToCurrentUser({
            equityLevel: trade1.equityLevel,
            loanAmount: trade1.loanAmount
              ? parseInt(trade1.loanAmount, 10)
              : undefined,
            status: trade1.status,
            vin: getTradeVin(trade1)
          })
        );
        if (addSecondTrade) {
          newAcquisitionIds.push(
            await addTradeToCurrentUser({
              equityLevel: trade2.equityLevel,
              loanAmount: trade2.loanAmount
                ? parseInt(trade2.loanAmount, 10)
                : undefined,
              status: trade2.status,
              vin: getTradeVin(trade2)
            })
          );
        }
      }
      if (softPullOpen) {
        await addSoftPullToCurrentUser({
          creditStatus: softPullCreditStatus,
          expired: softPullExpired
        });
      }
      const sessionUser = getCurrentSessionUser();
      if (sessionUser && newAcquisitionIds.length > 0) {
        const existing =
          sessionUser.acquisitionIds ??
          (sessionUser.acquisitionId ? [sessionUser.acquisitionId] : []);
        const merged = [...existing, ...newAcquisitionIds];
        const updated = {
          ...sessionUser,
          acquisitionId: merged[0],
          acquisitionIds: merged
        };
        setSessionUser(updated);
        storeTestUserInLocalStorage(updated);
      }
      window.location.reload();
    } catch (error) {
      setErrorMessage(`Failed to update user: ${error}`);
      setIsUpdating(false);
    }
  }, [
    addSecondTrade,
    softPullCreditStatus,
    softPullExpired,
    softPullOpen,
    trade1,
    trade2,
    tradeOpen
  ]);

  const handleLoginUser = useCallback(async (user: CreatedUser) => {
    try {
      await loginUser(user);
    } catch (error) {
      setErrorMessage(`Failed to login: ${error}`);
    }
  }, []);

  const handleCloseExpirePanel = useCallback(() => {
    setExpirePanelOpen(false);
    setExpireStatus('idle');
    setExpireError('');
    setExpireAcquisitionId('');
  }, []);

  const handleCopyAcquisitionId = useCallback(() => {
    if (!expireAcquisitionId) return;
    navigator.clipboard.writeText(expireAcquisitionId).catch(() => {
      // clipboard may be unavailable in some contexts; ignore
    });
  }, [expireAcquisitionId]);

  const handleOpenTestBot = useCallback(() => {
    window.open(
      'https://apps.carvanatech.com/qe/testlane/testbot',
      '_blank',
      'noopener,noreferrer'
    );
  }, []);

  const handleExpireTrade = useCallback(() => {
    const prePopup = openExpireLookupPopup();
    setExpirePanelOpen(true);
    setExpireStatus('idle');
    setExpireError('');
    setExpireAcquisitionId('');
    void runExpireFlow(prePopup);
  }, [runExpireFlow]);

  const handleCreateAndLoginClick = useCallback(() => {
    const needsExpirePopup = willApplyTrade && trade1.status === 'expired';
    const prePopup = needsExpirePopup ? openExpireLookupPopup() : null;
    void handleCreateAndLogin(prePopup);
  }, [handleCreateAndLogin, trade1.status, willApplyTrade]);

  const handleViewExistingUsers = useCallback(() => {
    const storedUsers = getTestUsersFromLocalStorage();
    const usersArray = Object.values(storedUsers).sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      const validDateA = isNaN(dateA) ? 0 : dateA;
      const validDateB = isNaN(dateB) ? 0 : dateB;

      return validDateB - validDateA;
    });
    setExistingUsers(usersArray);
    setShowExistingUsers(true);
  }, []);

  const handleClearUsers = useCallback(() => {
    if (window.confirm('Are you sure you want to delete all saved users?')) {
      clearTestUsersFromLocalStorage();
      setExistingUsers([]);
    }
  }, []);

  const handleUpdateUserState = useCallback((updatedUser: CreatedUser) => {
    setExistingUsers(prev =>
      prev.map(u => (u.email === updatedUser.email ? updatedUser : u))
    );
  }, []);

  const tradeConfigsValid =
    !isPendingMissingVin(trade1) &&
    (!addSecondTrade || !isPendingMissingVin(trade2));

  const trade1IsExpired = willApplyTrade && trade1.status === 'expired';
  const canCreate =
    !isCreating && !isUpdating && (!willApplyTrade || tradeConfigsValid);
  // Create (no login) can't finish the expire step — only Create & Login can.
  const canCreateNoLogin = canCreate && !trade1IsExpired;

  const canUpdateUser =
    isLoggedIn &&
    !isUpdating &&
    (softPullOpen || tradeOpen) &&
    (!tradeOpen || tradeConfigsValid);

  if (showExistingUsers) {
    return (
      <UsersList
        onBack={() => setShowExistingUsers(false)}
        onClear={handleClearUsers}
        onLogin={handleLoginUser}
        onUpdateUser={handleUpdateUserState}
        showClearButton={true}
        title="Existing Users"
        users={existingUsers}
      />
    );
  }

  if (createdUser) {
    return (
      <UsersList
        onBack={() => setCreatedUser(null)}
        onLogin={handleLoginUser}
        onUpdateUser={updatedUser => setCreatedUser(updatedUser)}
        showClearButton={false}
        title="User Created"
        users={[createdUser]}
      />
    );
  }

  return (
    <div className="flex flex-col gap-lg">
      <Heading>Create Common User</Heading>

      <FormField label="User Name (Optional)" htmlFor="user-name">
        <Input
          id="user-name"
          type="text"
          value={name}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setName(e.target.value)
          }
          placeholder="e.g. My Test User"
        />
      </FormField>

      {errorMessage && <div className="text-[#feaaaa]">{errorMessage}</div>}

      <div className="flex flex-wrap gap-8">
        <StyledButton
          type="button"
          onClick={handleCreate}
          data-analytics-id={null}
          disabled={!canCreateNoLogin}
          data-testid="create-user"
        >
          {isCreating ? 'Creating...' : 'Create'}
        </StyledButton>
        <StyledButton
          type="button"
          onClick={handleCreateAndLoginClick}
          data-analytics-id={null}
          disabled={!canCreate}
          data-testid="create-and-login-user"
        >
          {isCreating ? 'Creating...' : 'Create & Login'}
        </StyledButton>
        <StyledButton
          type="button"
          onClick={handleExpireTrade}
          data-analytics-id={null}
          disabled={!isLoggedIn || !sessionHasTrade || expirePanelOpen}
          data-testid="expire-trade"
        >
          Expire Trade
        </StyledButton>
        <StyledButton
          type="button"
          onClick={handleUpdateUser}
          data-analytics-id={null}
          disabled={!canUpdateUser}
          data-testid="update-user"
        >
          {isUpdating ? 'Updating...' : 'Update User'}
        </StyledButton>
        <StyledButton
          type="button"
          onClick={handleViewExistingUsers}
          data-analytics-id={null}
          className="ml-auto bg-gray-600 text-white hover:bg-gray-700"
          data-testid="view-existing-users"
        >
          Existing Users
        </StyledButton>
        {isLoggedIn && (
          <StyledButton
            type="button"
            onClick={() => logoutUser()}
            data-analytics-id={null}
            className="bg-gray-600 text-white hover:bg-gray-700"
            data-testid="logout-user"
          >
            Logout
          </StyledButton>
        )}
      </div>

      {expirePanelOpen && (
        <div
          className="flex flex-col gap-8 rounded border border-gray-600 p-sm"
          data-testid="expire-trade-panel"
        >
          {expireStatus === 'looking-up' && (
            <div className="text-xs text-gray-300">
              Looking up acquisition ID via /sell-my-car popup...
            </div>
          )}
          {expireStatus === 'ready' && expireAcquisitionId && (
            <>
              <div className="text-sm text-white">
                Acquisition ID:{' '}
                <span
                  className="font-mono text-blue-300"
                  data-testid="expire-acquisition-id"
                >
                  {expireAcquisitionId}
                </span>
              </div>
              <div className="text-xs text-gray-300">
                {
                  "Auto-expire isn't available on TEST yet. Copy the ID and finish the expire in TestBot."
                }
              </div>
              <div className="flex flex-wrap gap-8">
                <StyledButton
                  type="button"
                  onClick={handleCopyAcquisitionId}
                  data-analytics-id={null}
                  data-testid="copy-acquisition-id"
                >
                  Copy ID
                </StyledButton>
                <StyledButton
                  type="button"
                  onClick={handleOpenTestBot}
                  data-analytics-id={null}
                  data-testid="open-testbot"
                >
                  Open TestBot
                </StyledButton>
              </div>
            </>
          )}
          {expireError && (
            <div className="text-xs text-[#feaaaa]">{expireError}</div>
          )}
          <StyledButton
            type="button"
            onClick={handleCloseExpirePanel}
            data-analytics-id={null}
            className="bg-gray-600 text-white hover:bg-gray-700"
            data-testid="close-expire-panel"
          >
            Close
          </StyledButton>
        </div>
      )}

      <SoftPullSection
        applyOnCreate={softPullApplyOnCreate}
        creditStatus={softPullCreditStatus}
        expired={softPullExpired}
        isLoggedIn={isLoggedIn}
        isOpen={softPullOpen}
        onApplyOnCreateChange={setSoftPullApplyOnCreate}
        onCreditStatusChange={setSoftPullCreditStatus}
        onExpiredChange={setSoftPullExpired}
        onOpenChange={setSoftPullOpen}
      />
      <TradeSection
        addSecondTrade={addSecondTrade}
        applyOnCreate={tradeApplyOnCreate}
        isLoggedIn={isLoggedIn}
        isOpen={tradeOpen}
        onAddSecondTradeChange={setAddSecondTrade}
        onApplyOnCreateChange={setTradeApplyOnCreate}
        onOpenChange={setTradeOpen}
        onTrade1Change={setTrade1}
        onTrade2Change={setTrade2}
        trade1={trade1}
        trade2={trade2}
      />
    </div>
  );
}
